import React, {
  useEffect,
  useRef,
  useState,
  useCallback,
} from "react"
import {
  Box,
  Button,
  TextField,
  useMediaQuery,
} from "@mui/material"
import { Link } from "react-router-dom"
import IconButton from "@mui/material/IconButton"
import ImageIcon from "@mui/icons-material/Image"
import ClearIcon from "@mui/icons-material/Clear"
import Axios from "../../Util/Axios"
import { uploadFileToAws } from "../../Util/Aws"
import { updateToastData } from "../../slices/toastSlice"
import {
  TOAST_SEVERITY_ERROR,
  TOAST_SEVERITY_SUCCESS,
} from "../../components/core/Toast/Toast"
import { useDispatch } from "react-redux"
import "./Home.css"
import SwellWidget from "../SwellWidget/SwellWidget"

/* ── FAQ data ── */
const FAQ_ITEMS = [
  {
    q: "What should I get?",
    a: "Depends on where you surf and how you surf. Tell me your local break, what you're riding now, and what's missing — too stiff, too loose, can't get into waves, whatever it is. If you're not sure, send a message.",
  },
  {
    q: "How does the custom order process work?",
    a: "Feel free to reach out, fill out an order form, or send a message to get things started. I'll follow up directly, and we can talk through the details. I take half the total up front as a deposit. Other half is due when the board is finished.",
  },
  {
    q: "What is the deposit and payment structure?",
    a: "50% deposit to start, remaining balance at pickup. Cash, Venmo, bank transfer, or credit cards are accepted. Deposits are non-refundable.",
  },
  {
    q: "How long is the turnaround for a custom?",
    a: "Most builds are done in 2–4 weeks once you're in the queue but can change due to season. Ordering certain woods, multiple stringers, or other options may add another week or two. You'll get updates along the way.",
  },
  {
    q: "Do you glass or repair other shapes?",
    a: "Yes. If you've got a shaped blank that needs glassing, bring it in. I glass boards I didn't shape — repairs, reglasses, or fresh shapes from other shapers. Reach out first so we can talk through the work and get you a quote.",
  },
  {
    q: "What foam and glass is available?",
    a: "Polyurethane only — no EPS. Standard glass is 6 oz on larger boards, but any cloth weight is available depending on what the build calls for. Polyester resin only, no epoxy.",
  },
  {
    q: "Do you offer shipping?",
    a: "Boards are picked up in Santa Barbara. I can sometimes deliver within SB, Ventura, and SLO counties — just ask.",
  },
]

/* ── Intersection Observer hook for scroll animations ── */
function useScrollAnimation() {
  const observerRef = useRef(null)

  useEffect(() => {
    observerRef.current = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("visible")
          }
        })
      },
      { threshold: 0.12, rootMargin: "0px 0px -40px 0px" }
    )

    const targets = document.querySelectorAll(".animate-on-scroll")
    targets.forEach((el) => observerRef.current.observe(el))

    return () => {
      if (observerRef.current) observerRef.current.disconnect()
    }
  }, [])
}

/* ── FAQ Accordion Item ── */
const FaqItem = ({ question, answer }) => {
  const [open, setOpen] = useState(false)
  return (
    <div className={`faq-item ${open ? "open" : ""}`}>
      <button
        className="faq-question-button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
      >
        <span className="faq-question-text">{question}</span>
        <span className="faq-icon" aria-hidden="true" />
      </button>
      <div className="faq-answer" aria-hidden={!open}>
        <p className="faq-answer-inner">{answer}</p>
      </div>
    </div>
  )
}

const S3 = "https://marlinsurf-assets.s3.us-west-1.amazonaws.com"

/* ── Parse board dimensions from an IG caption ── */
function parseDimensions(caption) {
  if (!caption) return null
  // Normalise curly quotes → straight quotes before matching
  const c = caption.replace(/[\u2018\u2019\u2032]/g, "'").replace(/[\u201C\u201D\u2033]/g, '"')
  // Match patterns like: 9'2"x23"x2 7/8"  or  5'8" × 19 × 2.25
  const m = c.match(
    /(\d+)'(\d+(?:\.\d+)?)"?\s*[xX×]\s*([\d.]+)"?\s*[xX×]\s*([\d.]+(?:\s+\d+\/\d+)?)"?/
  )
  if (m) return `${m[1]}'${m[2]}" × ${m[3]}" × ${m[4]}"`
  return null
}

/* ── Sticky image section: image locks at top while scrolling through,
   text fades in at ~50% scroll reveal, then image scrolls away normally ── */
/* Each image is sticky at a lower z-layer than the previous one.
   It sits motionless while the previous item scrolls off the top,
   then becomes the "fixed" foreground until its own sticky releases. */
const StickyImageSection = ({ src, alt, zIndex, children }) => {
  const wrapperRef = useRef(null)
  const textRef = useRef(null)

  useEffect(() => {
    const wrapper = wrapperRef.current
    const text = textRef.current
    if (!wrapper || !text) return

    const check = () => {
      const rect = wrapper.getBoundingClientRect()
      // Text fades in halfway through the dwell period
      if (rect.top <= -(window.innerHeight * 0.5)) {
        text.classList.add("si-text-visible")
      }
    }

    window.addEventListener("scroll", check, { passive: true })
    check()
    return () => window.removeEventListener("scroll", check)
  }, [])

  return (
    <div className="si-wrapper" ref={wrapperRef}>
      <div className="si-inner" style={{ zIndex: zIndex ?? "auto" }}>
        <img src={src || ""} alt={alt || ""} className="si-img" />
        <div className="si-overlay" />
        <div className="si-text" ref={textRef}>
          {children}
        </div>
      </div>
    </div>
  )
}

/* ── Contact form (inline, adapted from Contact.jsx) ── */
const ContactForm = () => {
  const dispatch = useDispatch()
  const [loading, setLoading] = useState(false)
  const [name, setName] = useState("")
  const [email, setEmail] = useState("")
  const [phone, setPhone] = useState("")
  const [message, setMessage] = useState("")
  const [messageSent, setMessageSent] = useState(false)
  const fileUploadRef = useRef(null)
  const [files, setFiles] = useState([])

  const handleSubmit = () => {
    setLoading(true)
    Axios.post("/api/site-configuration/action_contact/", {
      email,
      name,
      message,
      phone,
      files,
    })
      .then(() => {
        setLoading(false)
        setName("")
        setEmail("")
        setPhone("")
        setMessage("")
        setFiles([])
        setMessageSent(true)
        dispatch(
          updateToastData({
            message: "Message sent successfully",
            severity: TOAST_SEVERITY_SUCCESS,
          })
        )
      })
      .catch(() => {
        setLoading(false)
        dispatch(
          updateToastData({
            message: "Error sending message",
            severity: TOAST_SEVERITY_ERROR,
          })
        )
      })
  }

  return (
    <div className="contact-form-wrapper">
      {messageSent && (
        <p className="contact-sent-message">
          Message received — we'll be in touch within 24 hours.
        </p>
      )}
      <TextField
        disabled={loading}
        margin="normal"
        required
        fullWidth
        label="Name"
        value={name}
        onChange={(e) => setName(e.target.value)}
        size="small"
        sx={{ "& *": { fontFamily: "Josefin Sans, sans-serif" } }}
      />
      <TextField
        disabled={loading}
        margin="normal"
        required
        fullWidth
        label="Email"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        size="small"
        sx={{ "& *": { fontFamily: "Josefin Sans, sans-serif" } }}
      />
      <TextField
        disabled={loading}
        margin="normal"
        fullWidth
        label="Phone (optional)"
        value={phone}
        onChange={(e) => setPhone(e.target.value)}
        size="small"
        sx={{ "& *": { fontFamily: "Josefin Sans, sans-serif" } }}
      />
      <TextField
        disabled={loading}
        margin="normal"
        required
        fullWidth
        multiline
        rows={window.innerWidth < 600 ? 3 : 5}
        label="Message"
        value={message}
        onChange={(e) => setMessage(e.target.value)}
        sx={{ "& *": { fontFamily: "Josefin Sans, sans-serif" } }}
      />
      <Box
        mt={0.5}
        sx={{ display: "flex", justifyContent: "flex-end" }}
      >
        <IconButton
          color="primary"
          size="small"
          onClick={() => fileUploadRef.current.click()}
          title="Attach image"
        >
          <ImageIcon fontSize="small" />
        </IconButton>
      </Box>
      {files.length > 0 && (
        <Box mt={1} sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
          {files.map((fileObj, i) => (
            <Box
              key={i}
              sx={{ position: "relative", width: 64, height: 64 }}
            >
              <img
                src={fileObj.url}
                alt="attachment preview"
                style={{
                  width: 64,
                  height: 64,
                  objectFit: "cover",
                  display: "block",
                }}
              />
              <IconButton
                size="small"
                sx={{
                  position: "absolute",
                  top: -8,
                  right: -8,
                  color: "#fc3158",
                  background: "white",
                  padding: "2px",
                  "&:hover": { background: "#ffe0e6" },
                }}
                onClick={() => setFiles(files.filter((_, idx) => idx !== i))}
              >
                <ClearIcon sx={{ fontSize: 14 }} />
              </IconButton>
            </Box>
          ))}
        </Box>
      )}
      <Button
        variant="contained"
        disabled={loading || !email || !name}
        sx={{
          mt: 3,
          width: "100%",
          textTransform: "none",
          fontFamily: "Josefin Sans, sans-serif",
          fontSize: "0.7rem",
          letterSpacing: "0.25em",
          textTransform: "uppercase",
          backgroundColor: "#152737",
          "&:hover": { backgroundColor: "#0d1b2a" },
          borderRadius: 0,
          py: 1.5,
        }}
        onClick={handleSubmit}
      >
        Send Message
      </Button>
      <input
        style={{ display: "none" }}
        ref={fileUploadRef}
        type="file"
        multiple
        accept="image/*"
        onChange={(e) => {
          const newFiles = Array.from(e.target.files)
          const promises = newFiles.map((f) => uploadFileToAws(f))
          Axios.all(promises)
            .then((resps) => setFiles([...files, ...resps]))
            .catch((err) =>
              dispatch(
                updateToastData({ message: err, severity: TOAST_SEVERITY_ERROR })
              )
            )
        }}
      />
    </div>
  )
}

/* ══════════════════════════════════════════════════════════════
   HOME COMPONENT
   ══════════════════════════════════════════════════════════════ */
const Home = () => {
  const isMobile = useMediaQuery((theme) => theme.breakpoints.down("sm"))
  const [instagramPosts, setInstagramPosts] = useState([])
  const [stockBoards, setStockBoards] = useState([])
  const [displayPresets, setDisplayPresets] = useState([])
  const [scrollY, setScrollY] = useState(0)
  const [activeFaq, setActiveFaq] = useState(null)
  const vhRef = useRef(window.innerHeight)
  const lastScrollY = useRef(0)
  const lastScrollTime = useRef(performance.now())
  const scrollSpeed = useRef(0)
  const rafId = useRef(null)

  /* ── Media sources ── */
  const videoSource = "https://marlinsurf-assets.s3.us-west-1.amazonaws.com/marlinsplash2.mp4"

  const heroFallbackImage = isMobile
    ? "https://marlinsurf-assets.s3.us-west-1.amazonaws.com/blacknwhitesurf.PNG"
    : "https://marlinsurf-assets.s3.us-west-1.amazonaws.com/masklogo.jpg"

  /* ── VH fix for mobile + scroll tracker ── */
  useEffect(() => {
    const setVH = () => {
      const h = window.innerHeight
      document.documentElement.style.setProperty("--vh", `${h * 0.01}px`)
      vhRef.current = h
    }
    const onScroll = () => {
      const now = performance.now()
      const dt = now - lastScrollTime.current
      if (dt > 0) {
        scrollSpeed.current = Math.abs(window.scrollY - lastScrollY.current) / dt
      }
      lastScrollY.current = window.scrollY
      lastScrollTime.current = now
      if (rafId.current) return // already scheduled
      rafId.current = requestAnimationFrame(() => {
        rafId.current = null
        setScrollY(window.scrollY)
      })
    }
    setVH()
    window.addEventListener("resize", setVH)
    window.addEventListener("scroll", onScroll, { passive: true })
    return () => {
      window.removeEventListener("resize", setVH)
      window.removeEventListener("scroll", onScroll)
      if (rafId.current) cancelAnimationFrame(rafId.current)
    }
  }, [])

  /* ── Data fetching ── */
  useEffect(() => {
    const IG_TOKEN = "IGAAV9ffwbfZA9BZAFlHYTN4TkpjMnh6a01XMWR0SG4xeDRMd2cyY0UyR0w1dnNEY1U0c0YtdHJrR2FVeWNDU2NrYXozcDZAnQ2c2NlhoOUY2NU5jNDdqYWNhdndfWk43QThSOGVnME9IOTFrZA2NXZAjJ0Nk9qWkhiYW43ZATlTRTV2NAZDZD"

    const fetchStockBoardsDirect = () => {
      fetch(`https://graph.instagram.com/me/media?fields=id,caption,media_type,media_url,permalink&limit=50&access_token=${IG_TOKEN}`)
        .then(r => r.json())
        .then(data => {
          const boards = (data.data || []).filter(p =>
            (p.media_type === "IMAGE" || p.media_type === "CAROUSEL_ALBUM") &&
            p.caption?.toLowerCase().includes("up for grabs")
          )
          if (boards.length) setStockBoards(boards)
        })
        .catch(() => {})
    }

    const fetchInstagramDirect = () => {
      fetch(`https://graph.instagram.com/me/media?fields=id,caption,media_type,media_url,permalink,thumbnail_url&limit=50&access_token=${IG_TOKEN}`)
        .then(r => r.json())
        .then(data => {
          const posts = (data.data || []).filter(p => p.media_type === "IMAGE" || p.media_type === "CAROUSEL_ALBUM")
          if (posts.length) setInstagramPosts(posts)
          const boards = posts.filter(p => p.caption?.toLowerCase().includes("up for grabs"))
          if (boards.length) setStockBoards(boards)
        })
        .catch(() => {})
    }
    // Always fetch stock boards from the full 50-post pool
    fetchStockBoardsDirect()

    Axios.get("/api/site-configuration/1/action_get_instagram_posts/")
      .then(({ data }) => {
        if (data.posts?.length) {
          setInstagramPosts(data.posts)
        } else fetchInstagramDirect()
      })
      .catch(() => fetchInstagramDirect())
    Axios.get("/api/preset-list/?type=display")
      .then(({ data }) => setDisplayPresets(data.results))
      .catch(() => {})
  }, [])

  /* ── Scroll animations ── */
  useScrollAnimation()

  /* ── Smooth scroll helper ── */
  const scrollTo = useCallback((id) => {
    const el = document.getElementById(id)
    if (el) el.scrollIntoView({ behavior: "smooth" })
  }, [])

  /* ── Hero scroll indicator ── */
  const handleHeroScroll = () => {
    window.scrollBy({ top: window.innerHeight * 0.85, behavior: "smooth" })
  }

  /* ── Fixed→absolute image stack ──
     Each image is position:fixed (motionless) until scrollY passes its threshold,
     then switches to position:absolute so it scrolls away naturally.
     At the exact switch point absolute-top = scrollY → no visual jump.
     vh is locked to resize events only (not every render) to prevent iOS
     address-bar hide/show from shifting thresholds mid-scroll. */
  const vh = vhRef.current
  const slow = scrollSpeed.current < 3 // px/ms — content only fades in when not flying through
  // Hysteresis buffer: when scrolling up past a threshold, delay the
  // absolute→fixed switch by this many px to prevent rapid flapping.
  const scrollingDown = scrollY >= lastScrollY.current
  const buf = scrollingDown ? 0 : 30
  const img1Fixed    = scrollY < 1 * vh - buf
  const imgStockFixed = scrollY < 2 * vh - buf
  const img2Fixed    = scrollY < 3 * vh - buf
  const img3Fixed    = scrollY < 4 * vh - buf
  const imgSwellFixed = scrollY < 5 * vh - buf
  const img4Fixed    = scrollY < 6 * vh - buf

  const imgLayerStyle = (isFixed, thresholdPx) => ({
    position: isFixed ? "fixed" : "absolute",
    top: isFixed ? 0 : thresholdPx,
    left: 0,
    width: "100%",
    height: vh,
    overflow: "hidden",
    willChange: "transform",       // promote to own compositor layer
    backfaceVisibility: "hidden",  // force GPU compositing on mobile
  })

  return (
    <div className="home-page">
      {/* ══════════════════════════════════════
          SCROLL CONTAINER
          The hero (z-index 20) sits in normal flow and scrolls away.
          The 4 images are fixed beneath it (z 14→11) and switch to
          absolute one-by-one as scrollY passes each threshold.
          This 400vh spacer provides the scroll space for all 4 transitions.
         ══════════════════════════════════════ */}

      {/* 1. HERO — normal flow, z-index 20, scrolls away naturally */}
      <section
        className="hero-section"
        style={{ height: "calc(var(--vh, 1vh) * 100)", position: "relative", zIndex: 20 }}
      >
        <video
          className="hero-media"
          autoPlay
          loop
          playsInline
          muted
        >
          <source src={videoSource} type="video/mp4" />
        </video>

        <div className="hero-overlay" />

        <div className="hero-content">
          <h1 className="hero-brand-name">MARLIN SURFBOARDS</h1>
          <p className="hero-tagline">Handcrafted in Santa Barbara, California</p>

          {/* CTA Buttons */}
          <Box
            sx={{
              display: "flex",
              gap: 2,
              mt: 5,
              flexWrap: "wrap",
              justifyContent: "center",
              pointerEvents: "auto",
              animation: "heroFadeIn 1.6s cubic-bezier(0.25,0.46,0.45,0.94) 0.8s forwards",
              opacity: 0,
            }}
          >
            <Button
              variant="contained"
              component={Link}
              to="/custom-surfboard-order"
              sx={{
                fontFamily: "Josefin Sans, sans-serif",
                fontSize: { xs: "0.55rem", sm: "0.65rem" },
                letterSpacing: "0.28em",
                textTransform: "uppercase",
                backgroundColor: "#c8b99a",
                color: "#0d1b2a",
                borderRadius: 0,
                px: { xs: 2.5, sm: 4 },
                py: { xs: 1.2, sm: 1.5 },
                "&:hover": { backgroundColor: "#f5f2ec" },
                boxShadow: "none",
                minWidth: 0,
                whiteSpace: "nowrap",
              }}
            >
              Custom Order
            </Button>
            <Button
              variant="outlined"
              onClick={() => window.scrollTo({ top: window.innerHeight * 2, behavior: "smooth" })}
              sx={{
                fontFamily: "Josefin Sans, sans-serif",
                fontSize: { xs: "0.55rem", sm: "0.65rem" },
                letterSpacing: "0.28em",
                textTransform: "uppercase",
                color: "#f5f2ec",
                borderColor: "rgba(245, 242, 236, 0.5)",
                borderRadius: 0,
                px: { xs: 2.5, sm: 4 },
                py: { xs: 1.2, sm: 1.5 },
                "&:hover": {
                  backgroundColor: "rgba(245, 242, 236, 0.1)",
                  borderColor: "#f5f2ec",
                },
                boxShadow: "none",
                minWidth: 0,
                whiteSpace: "nowrap",
              }}
            >
              Stock Boards
            </Button>
          </Box>
        </div>

        {/* Scroll indicator */}
        <button
          className="hero-scroll-indicator"
          onClick={handleHeroScroll}
          aria-label="Scroll down"
          style={{ background: "none", border: "none" }}
        >
          <span>Scroll</span>
          <span className="scroll-arrow" />
        </button>
      </section>

      {/* Scroll spacer — 6×vh of scroll space for 6 image transitions */}
      <div style={{ height: 6 * vh }} />

      {/* ══════════════════════════════════════
          IMAGE 1  z=14 — motionless behind hero, revealed as hero exits.
          Switches to absolute at 1×vh so it scrolls away while image 2 holds.
         ══════════════════════════════════════ */}
      <div style={{ ...imgLayerStyle(img1Fixed, 1 * vh), zIndex: 14 }}>
        <img src={`${S3}/IMG_2436.jpg`} alt="Santa Barbara surf" className="si-img si-img--1" />
        <div className="si-overlay" />
        <div className={`si-text si-text--left si-text--lower${slow && scrollY > vh * 0.5 && scrollY < vh * 1.5 ? " si-text-visible" : ""}`}>
          <span className="si-eyebrow">Est. 2015 — Santa Barbara, CA</span>
          <h2 className="si-headline">Handcrafted for the wave.<br /><em>Built to last a lifetime.</em></h2>
          <p className="si-body si-body--img1">
            Shaping surfboards out of Santa Barbara since 2015. Customs built to your dimensions and stock boards ready to grab off the rack — every one of them hand-shaped, hand-glassed, and finished right here.
          </p>
          <p className="si-body si-body--img1">
            Shortboards to longboards, performance to cruiser. Whatever you're looking to ride, I'll shape it.
          </p>
        </div>
      </div>

      {/* ══════════════════════════════════════
          STOCK BOARDS PANEL  z=13
         ══════════════════════════════════════ */}
      <div style={{ ...imgLayerStyle(imgStockFixed, 2 * vh), zIndex: 13 }}>
        <img src={`${S3}/IMG_7582.jpg`} alt="Stock boards" className="si-img si-img--stock" />
        <div className="si-overlay si-overlay--stock" />
        <div className={`si-text si-text--stock${slow && scrollY > vh * 1.6 && scrollY < vh * 2.5 ? " si-text-visible" : ""}`}>
          <div className="si-stock-inner">
          <span className="si-eyebrow">Ready to Ride</span>
          <h2 className="si-headline si-headline--stock">Stock Boards.</h2>
          <p className="si-stock-sub">Can't wait to get in the water? Grab a board off the rack.</p>
          {stockBoards.length > 0 ? (
            <>
              <div className="si-stock-grid">
                {stockBoards.slice(0, 8).map((post, i) => {
                  const dims = parseDimensions(post.caption)
                  const isSold = i >= 2 || post.caption?.toLowerCase().includes("(sold)")
                  return (
                    <div
                      key={post.id}
                      className={`si-stock-card${isSold ? " si-stock-card--sold" : ""}`}
                    >
                      <a
                        href={post.permalink}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="si-stock-img-link"
                      >
                        <div className="si-stock-img-wrap">
                          <img
                            src={post.media_url}
                            alt={post.caption ? post.caption.slice(0, 60) : "Stock board"}
                            className="si-stock-img"
                            loading="lazy"
                          />
                        </div>
                      </a>
                      {dims && <p className="si-stock-dims">{dims}</p>}
                      {isSold && <p className="si-stock-sold">SOLD</p>}
                      {!isSold && (
                        <Link to="/#contact" className="si-stock-contact-btn">
                          Contact
                        </Link>
                      )}
                    </div>
                  )
                })}
              </div>
              <Link to="/stock-boards" className="si-cta-btn" style={{ marginTop: 32 }}>
                See More
              </Link>
            </>
          ) : (
            <p className="si-stock-empty">Check back soon — new boards drop regularly.</p>
          )}
          </div>
        </div>
      </div>

      {/* ══════════════════════════════════════
          IMAGE 2  z=12
         ══════════════════════════════════════ */}
      <div style={{ ...imgLayerStyle(img2Fixed, 3 * vh), zIndex: 12 }}>
        <img src={`${S3}/IMG_4542.JPG`} alt="Ocean waves" className="si-img" />
        <div className="si-overlay" />
        <div className={`si-text si-text--right${slow && scrollY > vh * 2.7 && scrollY < vh * 3.3 ? " si-text-visible" : ""}`}>
          <span className="si-eyebrow">FAQ</span>
          <h2 className="si-headline">Things worth knowing.</h2>
          <div className="si-faq-list">
            {FAQ_ITEMS.map((item, i) => (
              <button
                key={i}
                className={`si-faq-q${activeFaq === i ? " active" : ""}`}
                onClick={() => setActiveFaq(activeFaq === i ? null : i)}
              >
                {item.q}
              </button>
            ))}
          </div>
        </div>
        {/* Answer fades in at left */}
        <div className={`si-faq-answer${activeFaq !== null && scrollY > vh * 2.7 && scrollY < vh * 3.3 ? " visible" : ""}`}>
          {activeFaq !== null && (
            <>
              <span className="si-faq-answer-q">{FAQ_ITEMS[activeFaq].q}</span>
              <p className="si-faq-answer-text">{FAQ_ITEMS[activeFaq].a}</p>
            </>
          )}
        </div>
      </div>

      {/* ══════════════════════════════════════
          IMAGE 3  z=11
         ══════════════════════════════════════ */}
      <div style={{ ...imgLayerStyle(img3Fixed, 4 * vh), zIndex: 11 }}>
        <img src={`${S3}/DSC0118.jpg`} alt="Surfboard shaping" className="si-img si-img--3" />
        <div className="si-overlay" />
        <div className={`si-text si-text--instagram${slow && scrollY > vh * 3.8 && scrollY < vh * 4.4 ? " si-text-visible" : ""}`}>
          <div className="si-ig-layout">
            <div className="si-ig-left">
              <span className="si-eyebrow">Instagram — @marlin.surf</span>
              <h2 className="si-ig-header">Recent Work</h2>
              <div className="si-ig-grid">
                {instagramPosts.slice(0, 9).map((post) => (
                  <a
                    key={post.id}
                    href={post.permalink}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="si-ig-tile"
                  >
                    <img
                      src={post.media_url}
                      alt={post.caption ? post.caption.slice(0, 60) : "marlin.surf"}
                      className="si-ig-img"
                      loading="lazy"
                    />
                  </a>
                ))}
              </div>
              <div className="si-ig-right">
                <h3 className="si-ig-right-title">What We Shape</h3>
                <p className="si-ig-right-text">
                  Shortboards, fish, twin fins, mid-lengths, logs — 5'0" to 11'6". Custom builds shaped for the surfer who ordered them, plus stock boards ready to go.
                </p>
                <p className="si-ig-right-text">
                  If you've got something in your head that you can't find in a shop, that's what I like to build. If you're looking for ideas, feel free to take a look at the Instagram and see what's possible.
                </p>
              </div>
              <a
                href="https://www.instagram.com/marlin.surf"
                target="_blank"
                rel="noopener noreferrer"
                className="si-cta-btn"
                style={{ marginTop: 12 }}
              >
                Follow on Instagram
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* ══════════════════════════════════════
          SWELL FORECAST PANEL  z=9
         ══════════════════════════════════════ */}
      <div style={{ ...imgLayerStyle(imgSwellFixed, 5 * vh), zIndex: 9 }}>
        <img src={`${S3}/IMG_8508.JPG`} alt="Surf forecast" className="si-img si-img--swell" />
        <div className="si-overlay si-overlay--swell" />
        <div className={`si-text si-text--swell${slow && scrollY > vh * (isMobile ? 4.8 : 4.5) && scrollY < vh * 5.4 ? " si-text-visible" : ""}`}>
          <div className="si-swell-header">
            <span className="si-swell-eyebrow">Santa Barbara Surf Forecast</span>
            <h2 className="si-swell-headline">Check the Conditions</h2>
            <p className="si-swell-blurb">Live swell, wind, and tide data for local breaks — updated hourly.</p>
          </div>
          <SwellWidget />
        </div>
      </div>

      {/* ══════════════════════════════════════
          IMAGE 4  z=8
         ══════════════════════════════════════ */}
      <div id="contact" style={{ ...imgLayerStyle(img4Fixed, 6 * vh), zIndex: 8 }}>
        <img src={`${S3}/IMG_4536.JPG`} alt="Surfboard fins" className="si-img" />
        <div className="si-overlay" />
        <div className={`si-text si-text--form${slow && scrollY > vh * 5.8 ? " si-text-visible" : ""}`}>
          <div className="si-form-panel">
            <span className="si-eyebrow">Get in Touch</span>
            <h2 className="si-headline">Let's shape something together.</h2>
            <div className="si-form-row">
              <div className="si-form-col">
                <ContactForm />
              </div>
              <div className="si-contact-details">
                <p className="si-body" style={{ marginBottom: 24, marginTop: 0 }}>
                  Ready to order, got questions about a build, or just want to talk boards — drop a line. Let me know what you're looking for.
                </p>
                <div className="si-contact-item">
                  <h3 className="si-contact-label">Location</h3>
                  <span>Santa Barbara, CA</span>
                </div>
                <div className="si-contact-item">
                  <h3 className="si-contact-label">Instagram</h3>
                  <span>@marlin.surf</span>
                </div>
                <div className="si-contact-item">
                  <h3 className="si-contact-label">Email</h3>
                  <span>marlin@marlin.surf</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>




      {/* ══════════════════════════════════════
          11. FOOTER
         ══════════════════════════════════════ */}
      <footer className="home-footer">
        <div className="footer-inner">
          <p className="footer-copy">
            © {new Date().getFullYear()} Marlin Surfboards. All rights reserved.
            Handcrafted in Santa Barbara, California.
          </p>
        </div>
      </footer>
    </div>
  )
}

export default Home
